import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import toast from 'react-hot-toast';
import './SimTrade.css';

const ASSETS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'DOGE', 'AVAX'];
const ASSET_ICONS = { BTC: '₿', ETH: 'Ξ', SOL: '◎', BNB: '⬡', XRP: '✕', ADA: '₳', DOGE: 'Ð', AVAX: '▲' };
const LEVERAGE_OPTIONS = [1, 2, 5, 10, 20, 50];
const TICKER_MS   = 3000;
const POS_POLL_MS = 3000;

// ── Utility ───────────────────────────────────────────────────────────────────
const fmt  = (n, dp = 2) => Number(n).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
const fmtP = (n) => `${n >= 0 ? '+' : ''}${fmt(n)}%`;
const dp   = (asset) => ['XRP','ADA','DOGE'].includes(asset) ? 4 : 2;

// ── Candlestick SVG chart ─────────────────────────────────────────────────────
function CandlestickChart({ candles, asset }) {
  const svgRef   = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [dims, setDims]       = useState({ w: 800, h: 340 });

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: Math.floor(width), h: Math.floor(height) });
    });
    if (svgRef.current) obs.observe(svgRef.current);
    return () => obs.disconnect();
  }, []);

  if (!candles.length) return <div className="chart-loading">Loading chart…</div>;

  const visible = candles.slice(-80);
  const { w, h } = dims;
  const PAD = { t: 16, b: 28, l: 14, r: 58 };
  const chartW = w - PAD.l - PAD.r;
  const chartH = h - PAD.t - PAD.b;

  const prices = visible.flatMap(c => [c.h, c.l]);
  const minP   = Math.min(...prices);
  const maxP   = Math.max(...prices);
  const range  = maxP - minP || 1;

  const toX = i  => PAD.l + (i / (visible.length - 1 || 1)) * chartW;
  const toY = p  => PAD.t + chartH - ((p - minP) / range) * chartH;

  const candleW = Math.max(3, chartW / visible.length * 0.6);

  // Y-axis labels
  const yTicks = 5;
  const yLabels = Array.from({ length: yTicks }, (_, i) => {
    const val = minP + (range * i) / (yTicks - 1);
    return { y: toY(val), val };
  });

  // X-axis labels (every ~15 candles)
  const xStep = Math.max(1, Math.floor(visible.length / 5));
  const xLabels = visible
    .map((c, i) => ({ i, t: c.t }))
    .filter((_, i) => i % xStep === 0);

  // Close price line
  const closePts = visible.map((c, i) => `${toX(i)},${toY(c.c)}`).join(' ');

  return (
    <div className="chart-svg-wrap" ref={svgRef}>
      <svg width={w} height={h} className="chart-svg">
        {/* Grid lines */}
        {yLabels.map(({ y, val }, i) => (
          <g key={i}>
            <line x1={PAD.l} y1={y} x2={w - PAD.r} y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
            <text x={w - PAD.r + 6} y={y + 4} fill="#64748b" fontSize={10} fontFamily="monospace">
              {val >= 1000 ? (val >= 10000 ? `${(val/1000).toFixed(1)}K` : val.toFixed(0)) : val.toFixed(dp(asset))}
            </text>
          </g>
        ))}

        {/* X labels */}
        {xLabels.map(({ i, t }) => (
          <text key={i} x={toX(i)} y={h - 8} fill="#475569" fontSize={9} textAnchor="middle" fontFamily="monospace">
            {new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </text>
        ))}

        {/* Candles */}
        {visible.map((c, i) => {
          const x    = toX(i);
          const isUp = c.c >= c.o;
          const color = isUp ? '#10b981' : '#ef4444';
          const bodyTop = toY(Math.max(c.o, c.c));
          const bodyBot = toY(Math.min(c.o, c.c));
          const bodyH   = Math.max(1, bodyBot - bodyTop);

          return (
            <g key={i}
              onMouseEnter={() => setTooltip({ i, c, x, y: toY((c.h + c.l) / 2) })}
              onMouseLeave={() => setTooltip(null)}
            >
              {/* Wick */}
              <line x1={x} y1={toY(c.h)} x2={x} y2={toY(c.l)} stroke={color} strokeWidth={1} />
              {/* Body */}
              <rect
                x={x - candleW / 2} y={bodyTop}
                width={candleW} height={bodyH}
                fill={color} fillOpacity={0.85}
                rx={0.5}
              />
            </g>
          );
        })}

        {/* Latest price line */}
        {visible.length > 0 && (() => {
          const last = visible[visible.length - 1];
          const ly = toY(last.c);
          const isUp = last.c >= last.o;
          return (
            <g>
              <line x1={PAD.l} y1={ly} x2={w - PAD.r} y2={ly}
                stroke={isUp ? '#10b981' : '#ef4444'} strokeWidth={1}
                strokeDasharray="4 3" opacity={0.6}
              />
              <rect x={w - PAD.r + 2} y={ly - 9} width={52} height={16}
                fill={isUp ? '#10b981' : '#ef4444'} rx={3} />
              <text x={w - PAD.r + 28} y={ly + 4} fill="#fff" fontSize={9.5}
                textAnchor="middle" fontFamily="monospace" fontWeight="600">
                {last.c >= 1000 ? last.c.toFixed(2) : last.c.toFixed(dp(asset))}
              </text>
            </g>
          );
        })()}

        {/* Tooltip */}
        {tooltip && (() => {
          const { c, x } = tooltip;
          const boxW = 130, boxH = 80;
          const bx = x + 10 + boxW > w ? x - boxW - 10 : x + 10;
          const by = PAD.t;
          return (
            <g>
              <rect x={bx} y={by} width={boxW} height={boxH}
                fill="#0c1120" stroke="rgba(124,58,237,0.4)" strokeWidth={1} rx={6} />
              {[
                ['O', c.o, '#94a3b8'], ['H', c.h, '#10b981'],
                ['L', c.l, '#ef4444'], ['C', c.c, c.c >= c.o ? '#10b981' : '#ef4444'],
              ].map(([lbl, val, col], j) => (
                <g key={lbl}>
                  <text x={bx + 10} y={by + 18 + j * 16} fill="#64748b" fontSize={10} fontFamily="monospace">{lbl}</text>
                  <text x={bx + 24} y={by + 18 + j * 16} fill={col}    fontSize={10} fontFamily="monospace">
                    {val >= 1000 ? val.toFixed(2) : val.toFixed(dp(asset))}
                  </text>
                </g>
              ))}
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// ── Order Panel ───────────────────────────────────────────────────────────────
function OrderPanel({ asset, price, challenge, onOrderPlaced }) {
  const [side,       setSide]       = useState('BUY');
  const [size,       setSize]       = useState('');
  const [leverage,   setLeverage]   = useState(1);
  const [stopLoss,   setStopLoss]   = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [loading,    setLoading]    = useState(false);

  const margin    = size ? (Number(size) / leverage).toFixed(2) : '—';
  const notional  = size ? Number(size) : 0;
  const fee       = notional * 0.0004;
  const balance   = challenge?.currentBalance || 0;
  const balPct    = size ? Math.min(100, (Number(margin) / balance) * 100) : 0;

  const pctButtons = [25, 50, 75, 100];

  const handlePct = pct => {
    const val = (balance * pct / 100 * leverage).toFixed(2);
    setSize(val);
  };

  const handleSubmit = async () => {
    if (!size || Number(size) <= 0) return toast.error('Enter a position size');
    if (!challenge?._id)           return toast.error('No active challenge selected');
    setLoading(true);
    try {
      const { data } = await api.post('/simtrade/order', {
        challengeId: challenge._id,
        asset,
        side,
        size:        Number(size),
        leverage,
        stopLoss:    stopLoss    ? Number(stopLoss)    : undefined,
        takeProfit:  takeProfit  ? Number(takeProfit)  : undefined,
      });
      toast.success(data.message || 'Order placed');
      setSize(''); setStopLoss(''); setTakeProfit('');
      onOrderPlaced();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Order failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="order-panel">
      <div className="op-title">Place Order</div>

      {/* Buy / Sell */}
      <div className="op-side-toggle">
        <button className={`op-side-btn long ${side === 'BUY' ? 'active' : ''}`}  onClick={() => setSide('BUY')}>
          ▲ LONG
        </button>
        <button className={`op-side-btn short ${side === 'SELL' ? 'active' : ''}`} onClick={() => setSide('SELL')}>
          ▼ SHORT
        </button>
      </div>

      {/* Asset + price info */}
      <div className="op-asset-row">
        <span className="op-asset-label">{ASSET_ICONS[asset]} {asset}/USDT</span>
        <span className="op-price mono">${price >= 1 ? fmt(price) : price?.toFixed(4)}</span>
      </div>

      {/* Leverage */}
      <div className="op-field-label">Leverage</div>
      <div className="op-lev-row">
        {LEVERAGE_OPTIONS.map(l => (
          <button key={l} className={`op-lev-btn ${leverage === l ? 'active' : ''}`} onClick={() => setLeverage(l)}>
            {l}×
          </button>
        ))}
      </div>

      {/* Size */}
      <div className="op-field-label">Position Size (USD)</div>
      <div className="op-input-wrap">
        <span className="op-input-prefix">$</span>
        <input
          className="op-input"
          type="number"
          min="1"
          placeholder="0.00"
          value={size}
          onChange={e => setSize(e.target.value)}
        />
        <span className="op-input-suffix">USDT</span>
      </div>
      <div className="op-pct-row">
        {pctButtons.map(p => (
          <button key={p} className="op-pct-btn" onClick={() => handlePct(p)}>{p}%</button>
        ))}
      </div>

      {/* SL / TP */}
      <div className="op-sltp-row">
        <div className="op-sltp-field">
          <div className="op-field-label">Stop Loss</div>
          <input className="op-input-sm" type="number" placeholder={price ? (side === 'BUY' ? (price * 0.97).toFixed(dp(asset)) : (price * 1.03).toFixed(dp(asset))) : '—'}
            value={stopLoss} onChange={e => setStopLoss(e.target.value)} />
        </div>
        <div className="op-sltp-field">
          <div className="op-field-label">Take Profit</div>
          <input className="op-input-sm" type="number" placeholder={price ? (side === 'BUY' ? (price * 1.05).toFixed(dp(asset)) : (price * 0.95).toFixed(dp(asset))) : '—'}
            value={takeProfit} onChange={e => setTakeProfit(e.target.value)} />
        </div>
      </div>

      {/* Summary */}
      <div className="op-summary">
        <div className="op-sum-row"><span>Margin Required</span><span className="mono">${margin}</span></div>
        <div className="op-sum-row"><span>Trading Fee</span><span className="mono">${fee.toFixed(4)}</span></div>
        <div className="op-sum-row"><span>Notional Value</span><span className="mono">${notional ? fmt(notional) : '—'}</span></div>
        <div className="op-sum-row"><span>Leverage</span><span className="mono accent">{leverage}×</span></div>
      </div>

      {/* Balance usage bar */}
      {size && (
        <div className="op-bal-bar-wrap">
          <div className="op-bal-bar-track">
            <div className="op-bal-bar-fill" style={{ width: `${Math.min(100, balPct)}%`, background: balPct > 80 ? '#ef4444' : '#7c3aed' }} />
          </div>
          <span className="op-bal-pct">{balPct.toFixed(1)}% of balance</span>
        </div>
      )}

      {/* Balance display */}
      <div className="op-balance">
        Available: <span className="mono">${fmt(balance)}</span>
      </div>

      <button
        className={`op-submit ${side === 'BUY' ? 'long' : 'short'} ${loading ? 'loading' : ''}`}
        onClick={handleSubmit}
        disabled={loading || !size || !challenge}
      >
        {loading ? '…' : `${side === 'BUY' ? '▲ Open Long' : '▼ Open Short'} ${asset}`}
      </button>
    </div>
  );
}

// ── Positions table ───────────────────────────────────────────────────────────
function PositionsTable({ positions, onClose, closing }) {
  if (!positions.length) {
    return (
      <div className="pos-empty">
        <span>No open positions</span>
        <p>Place an order above to open your first position.</p>
      </div>
    );
  }

  return (
    <div className="pos-table-wrap">
      <table className="pos-table">
        <thead>
          <tr>
            <th>Asset</th>
            <th>Side</th>
            <th>Size</th>
            <th>Lev</th>
            <th>Entry</th>
            <th>Mark</th>
            <th>P&amp;L</th>
            <th>Margin</th>
            <th>Liq.</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <AnimatePresence initial={false}>
            {positions.map(pos => (
              <motion.tr
                key={pos._id}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className={`pos-row ${pos.livePnl >= 0 ? 'profit' : 'loss'}`}
              >
                <td><span className="pos-asset-icon">{ASSET_ICONS[pos.asset]}</span>{pos.asset}</td>
                <td><span className={`pos-side-badge ${pos.side === 'BUY' ? 'long' : 'short'}`}>{pos.side === 'BUY' ? 'LONG' : 'SHORT'}</span></td>
                <td className="mono">${fmt(pos.size)}</td>
                <td className="mono accent">{pos.leverage}×</td>
                <td className="mono">${pos.entryPrice >= 1 ? fmt(pos.entryPrice) : pos.entryPrice.toFixed(4)}</td>
                <td className="mono">${pos.currentPrice >= 1 ? fmt(pos.currentPrice) : pos.currentPrice.toFixed(4)}</td>
                <td className={`mono bold ${pos.livePnl >= 0 ? 'green' : 'red'}`}>
                  {pos.livePnl >= 0 ? '+' : ''}${fmt(pos.livePnl)}
                  <span className="pos-pnl-pct"> ({fmtP(pos.livePnlPct)})</span>
                </td>
                <td className="mono">${fmt(pos.margin)}</td>
                <td className="mono red-dim">${pos.liqPrice >= 1 ? fmt(pos.liqPrice) : pos.liqPrice?.toFixed(4)}</td>
                <td>
                  <button
                    className="pos-close-btn"
                    onClick={() => onClose(pos._id)}
                    disabled={closing === pos._id}
                  >
                    {closing === pos._id ? '…' : 'Close'}
                  </button>
                </td>
              </motion.tr>
            ))}
          </AnimatePresence>
        </tbody>
      </table>
    </div>
  );
}

// ── History table ─────────────────────────────────────────────────────────────
function HistoryTable({ trades }) {
  if (!trades.length) {
    return <div className="pos-empty"><span>No closed trades yet</span></div>;
  }

  return (
    <div className="pos-table-wrap">
      <table className="pos-table">
        <thead>
          <tr>
            <th>Asset</th>
            <th>Side</th>
            <th>Size</th>
            <th>Lev</th>
            <th>Entry</th>
            <th>Exit</th>
            <th>P&amp;L</th>
            <th>Fee</th>
            <th>Time</th>
          </tr>
        </thead>
        <tbody>
          {trades.map(t => (
            <tr key={t._id} className={`pos-row ${t.pnl >= 0 ? 'profit' : 'loss'}`}>
              <td><span className="pos-asset-icon">{ASSET_ICONS[t.asset]}</span>{t.asset}</td>
              <td><span className={`pos-side-badge ${t.side === 'BUY' ? 'long' : 'short'}`}>{t.side === 'BUY' ? 'LONG' : 'SHORT'}</span></td>
              <td className="mono">${fmt(t.size)}</td>
              <td className="mono accent">{t.leverage}×</td>
              <td className="mono">${t.entryPrice >= 1 ? fmt(t.entryPrice) : t.entryPrice.toFixed(4)}</td>
              <td className="mono">${t.exitPrice >= 1 ? fmt(t.exitPrice) : t.exitPrice?.toFixed(4)}</td>
              <td className={`mono bold ${t.pnl >= 0 ? 'green' : 'red'}`}>
                {t.pnl >= 0 ? '+' : ''}${fmt(t.pnl)}
                <span className="pos-pnl-pct"> ({fmtP(t.pnlPct)})</span>
              </td>
              <td className="mono dim">${fmt(t.fee, 4)}</td>
              <td className="dim small">{t.closedAt ? new Date(t.closedAt).toLocaleTimeString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main SimTrade Page ────────────────────────────────────────────────────────
export default function SimTrade() {
  const [selectedAsset, setSelectedAsset] = useState('BTC');
  const [candles,       setCandles]       = useState([]);
  const [ticker,        setTicker]        = useState({});
  const [positions,     setPositions]     = useState([]);
  const [history,       setHistory]       = useState([]);
  const [challenge,     setChallenge]     = useState(null);
  const [challenges,    setChallenges]    = useState([]);
  const [bottomTab,     setBottomTab]     = useState('positions');
  const [closing,       setClosing]       = useState(null);
  const [loadingInit,   setLoadingInit]   = useState(true);

  const tickerRef = useRef(null);
  const posRef    = useRef(null);

  // ── Load challenges on mount ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/challenges');
        const active = data.challenges?.filter(c => ['active','phase2'].includes(c.status)) || [];
        setChallenges(active);
        if (active.length) setChallenge(active[0]);
      } catch (_) {
        toast.error('Could not load challenges');
      } finally {
        setLoadingInit(false);
      }
    })();
  }, []);

  // ── Candle fetch ────────────────────────────────────────────────────────────
  const fetchCandles = useCallback(async () => {
    try {
      const { data } = await api.get(`/simtrade/candles/${selectedAsset}?limit=100`);
      setCandles(data.candles || []);
    } catch (_) {}
  }, [selectedAsset]);

  // ── Ticker fetch ────────────────────────────────────────────────────────────
  const fetchTicker = useCallback(async () => {
    try {
      const { data } = await api.get('/simtrade/ticker');
      setTicker(data.ticker || {});
    } catch (_) {}
  }, []);

  // ── Positions fetch ─────────────────────────────────────────────────────────
  const fetchPositions = useCallback(async () => {
    if (!challenge?._id) return;
    try {
      const { data } = await api.get(`/simtrade/positions/${challenge._id}`);
      setPositions(data.positions || []);

      // Auto-close SL/TP hits
      if (data.autoClosedIds?.length) {
        for (const id of data.autoClosedIds) {
          await api.post(`/simtrade/close/${id}`);
          toast(`SL/TP triggered — position closed`, { icon: '⚡' });
        }
        fetchPositions();
      }
    } catch (_) {}
  }, [challenge]);

  // ── History fetch ───────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    if (!challenge?._id) return;
    try {
      const { data } = await api.get(`/simtrade/history/${challenge._id}?limit=50`);
      setHistory(data.trades || []);
    } catch (_) {}
  }, [challenge]);

  // ── Initial data load ───────────────────────────────────────────────────────
  useEffect(() => {
    fetchCandles();
    fetchTicker();
  }, [fetchCandles, fetchTicker]);

  useEffect(() => {
    fetchPositions();
    fetchHistory();
  }, [fetchPositions, fetchHistory]);

  // ── Polling intervals ───────────────────────────────────────────────────────
  useEffect(() => {
    tickerRef.current = setInterval(() => {
      fetchCandles();
      fetchTicker();
    }, TICKER_MS);
    return () => clearInterval(tickerRef.current);
  }, [fetchCandles, fetchTicker]);

  useEffect(() => {
    posRef.current = setInterval(fetchPositions, POS_POLL_MS);
    return () => clearInterval(posRef.current);
  }, [fetchPositions]);

  // ── Close position ──────────────────────────────────────────────────────────
  const handleClose = async (tradeId) => {
    setClosing(tradeId);
    try {
      const { data } = await api.post(`/simtrade/close/${tradeId}`);
      const pnl = data.pnl;
      toast[pnl >= 0 ? 'success' : 'error'](
        `Position closed — ${pnl >= 0 ? '+' : ''}$${Math.abs(pnl).toFixed(2)} P&L`
      );
      // Refresh challenge balance
      const { data: cd } = await api.get('/challenges');
      const active = cd.challenges?.filter(c => ['active','phase2'].includes(c.status)) || [];
      const updated = active.find(c => c._id === challenge._id);
      if (updated) setChallenge(updated);
      fetchPositions();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to close');
    } finally {
      setClosing(null);
    }
  };

  const currentInfo = ticker[selectedAsset] || {};
  const currentPrice = currentInfo.price || 0;
  const changeUp = (currentInfo.changePct || 0) >= 0;

  const posCount = positions.length;
  const totalPnL = positions.reduce((s, p) => s + (p.livePnl || 0), 0);

  // ── No active challenge state ───────────────────────────────────────────────
  if (!loadingInit && !challenge) {
    return (
      <div className="sim-layout">
        <Sidebar />
        <div className="sim-main">
          <div className="sim-no-challenge">
            <div className="snc-icon">📊</div>
            <h2>No Active Challenge</h2>
            <p>You need an active challenge to use the trading engine.</p>
            <Link to="/pricing" className="btn btn-primary">Start a Challenge →</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sim-layout">
      <Sidebar />

      <div className="sim-main">
        {/* ── Top header ─────────────────────────────────────────────────────── */}
        <div className="sim-header">
          <div className="sim-header-left">
            <h1 className="sim-title">
              <span className="sim-title-icon">📈</span>
              Sim Trading Engine
            </h1>
            {challenge && (
              <div className="sim-challenge-badge">
                <span className="scb-dot" />
                {challenge.plan?.name || 'Active Challenge'} · ${Number(challenge.currentBalance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                {challenges.length > 1 && (
                  <select
                    className="sim-chal-select"
                    value={challenge._id}
                    onChange={e => {
                      const c = challenges.find(x => x._id === e.target.value);
                      if (c) setChallenge(c);
                    }}
                  >
                    {challenges.map(c => (
                      <option key={c._id} value={c._id}>{c.currentBalance?.toFixed(0)} — {c.status}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* Live P&L summary */}
          {posCount > 0 && (
            <div className={`sim-live-pnl ${totalPnL >= 0 ? 'green' : 'red'}`}>
              <span className="slp-dot" />
              {posCount} open · {totalPnL >= 0 ? '+' : ''}${fmt(totalPnL)} unrealized
            </div>
          )}
        </div>

        {/* ── Asset tabs ─────────────────────────────────────────────────────── */}
        <div className="sim-asset-bar">
          {ASSETS.map(a => {
            const info = ticker[a] || {};
            const up   = (info.changePct || 0) >= 0;
            return (
              <button
                key={a}
                className={`asset-tab ${selectedAsset === a ? 'active' : ''}`}
                onClick={() => setSelectedAsset(a)}
              >
                <span className="at-icon">{ASSET_ICONS[a]}</span>
                <span className="at-name">{a}</span>
                <span className="at-price mono">{info.price >= 1 ? `$${info.price?.toFixed(2)}` : `$${info.price?.toFixed(4)}`}</span>
                <span className={`at-chg ${up ? 'green' : 'red'}`}>{up ? '+' : ''}{info.changePct?.toFixed(2)}%</span>
              </button>
            );
          })}
        </div>

        {/* ── Price header ────────────────────────────────────────────────────── */}
        <div className="sim-price-header">
          <div className="sph-left">
            <span className="sph-icon">{ASSET_ICONS[selectedAsset]}</span>
            <span className="sph-pair">{selectedAsset}/USDT</span>
            <AnimatePresence mode="wait">
              <motion.span
                key={currentPrice}
                className={`sph-price ${changeUp ? 'green' : 'red'}`}
                initial={{ opacity: 0.4, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
              >
                {currentPrice >= 1 ? `$${fmt(currentPrice)}` : `$${currentPrice.toFixed(4)}`}
              </motion.span>
            </AnimatePresence>
            <span className={`sph-change ${changeUp ? 'green' : 'red'}`}>
              {changeUp ? '▲' : '▼'} {Math.abs(currentInfo.changePct || 0).toFixed(2)}%
            </span>
          </div>
          <div className="sph-stats">
            <div className="sph-stat"><span>24H High</span><span className="mono green">${currentInfo.high >= 1 ? fmt(currentInfo.high) : currentInfo.high?.toFixed(4)}</span></div>
            <div className="sph-stat"><span>24H Low</span><span className="mono red">${currentInfo.low >= 1 ? fmt(currentInfo.low) : currentInfo.low?.toFixed(4)}</span></div>
            <div className="sph-stat"><span>Volume</span><span className="mono">{Number(currentInfo.volume || 0).toLocaleString()}</span></div>
            <div className="sph-stat"><span>Change</span><span className={`mono ${changeUp ? 'green' : 'red'}`}>{changeUp ? '+' : ''}{fmt(currentInfo.change || 0, currentPrice >= 1 ? 2 : 4)}</span></div>
          </div>
        </div>

        {/* ── Chart + Order Panel ─────────────────────────────────────────────── */}
        <div className="sim-body">
          <div className="sim-chart-area">
            <CandlestickChart candles={candles} asset={selectedAsset} />
          </div>
          <div className="sim-order-area">
            <OrderPanel
              asset={selectedAsset}
              price={currentPrice}
              challenge={challenge}
              onOrderPlaced={() => { fetchPositions(); fetchHistory(); }}
            />
          </div>
        </div>

        {/* ── Bottom panel ────────────────────────────────────────────────────── */}
        <div className="sim-bottom">
          <div className="sim-bottom-tabs">
            <button className={`sbt ${bottomTab === 'positions' ? 'active' : ''}`} onClick={() => setBottomTab('positions')}>
              Open Positions {posCount > 0 && <span className="sbt-badge">{posCount}</span>}
            </button>
            <button className={`sbt ${bottomTab === 'history' ? 'active' : ''}`} onClick={() => setBottomTab('history')}>
              Trade History {history.length > 0 && <span className="sbt-badge dim">{history.length}</span>}
            </button>
          </div>

          <div className="sim-bottom-content">
            {bottomTab === 'positions'
              ? <PositionsTable positions={positions} onClose={handleClose} closing={closing} />
              : <HistoryTable   trades={history} />
            }
          </div>
        </div>
      </div>
    </div>
  );
}
